import React, { useRef } from 'react';
import { Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Geom, xOf, wOf } from './ganttGeometry';
import { GanttEntry } from './ganttTypes';
import { BAR_REVEAL, MIN_BAR_PX, DRAG_THRESHOLD_PX, GAP_ANIM_MS, TOKENS, hasCoating } from './ganttConfig';

interface Props {
  e: GanttEntry;
  g: Geom;
  top: number;
  hPx: number;
  selected: boolean;
  showSeps: boolean;
  dragging?: boolean;
  /**
   * Live px offset while a drag is opening a gap in front of this bar — the
   * "make room" motion. Purely visual: absStart is untouched until the drop
   * commits and the server re-cascades.
   */
  shiftPx?: number;
  /** Stagger for the gap motion, so the push reads as a wave down the lane. */
  shiftDelayMs?: number;
  /** Ink colour filter active and this bar doesn't carry the ink → fade back. */
  inkDimmed?: boolean;
  flags?: string[];
  onHover: (e: GanttEntry, ev: React.MouseEvent) => void;
  onLeave: () => void;
  onSelect: (id: number) => void;
  onBarMenu: (e: GanttEntry, el: HTMLElement) => void;
  onBarGrab: (e: GanttEntry, ev: React.PointerEvent) => void;
}

export function fmtClock(min: number): string {
  const mm = ((min % 1440) + 1440) % 1440;
  const h = Math.floor(mm / 60);
  const m = Math.floor(mm) % 60;
  const ap = h >= 12 ? 'pm' : 'am';
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, '0')}${ap}`;
}

export default function GanttBar({
  e, g, top, hPx, selected, showSeps, dragging, shiftPx = 0, shiftDelayMs = 0, inkDimmed, flags,
  onHover, onLeave, onSelect, onBarMenu, onBarGrab,
}: Props) {
  const movable = e.status === 'planned';
  const didDragRef = useRef(false);
  const left = xOf(e.absStart, g);
  const width = Math.max(wOf(e.planned_total, g), MIN_BAR_PX);
  const st = e.style;

  // Running segments (>1 = the run pauses across a closed downtime band and
  // resumes). Fills are drawn per-segment so the closure shows through the gap;
  // labels reveal against the HEAD segment's width (where they actually sit),
  // not the full envelope, so a split bar doesn't float text over the pause.
  const segs = e.segments && e.segments.length ? e.segments : [{ startMin: e.absStart, endMin: e.absEnd }];
  const isSplit = segs.length > 1;
  const segRects = segs.map((s) => ({ x: xOf(s.startMin, g) - left, w: Math.max(wOf(s.endMin - s.startMin, g), 3) }));
  const headW = segRects[0]?.w ?? width;

  // Press-and-move: distinguish a click (select + menu) from a drag (move).
  // We only hand off to the parent once the pointer travels past the
  // threshold, so a plain click still opens the action menu.
  const onPointerDown = (ev: React.PointerEvent) => {
    if (!movable || ev.button !== 0) return;
    const sx = ev.clientX;
    const sy = ev.clientY;
    const start = ev;
    const move = (m: PointerEvent) => {
      if (Math.hypot(m.clientX - sx, m.clientY - sy) <= DRAG_THRESHOLD_PX) return;
      cleanup();
      didDragRef.current = true;
      onBarGrab(e, start);
    };
    const up = () => cleanup();
    const cleanup = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };
  const flagged = !!(flags && flags.length);
  const showJob = headW >= BAR_REVEAL.job.w && hPx >= BAR_REVEAL.job.h;
  const showCust = headW >= BAR_REVEAL.cust.w;
  const showDesc = headW >= BAR_REVEAL.desc.w && hPx >= BAR_REVEAL.desc.h;
  const showMeta = headW >= BAR_REVEAL.meta.w && hPx >= BAR_REVEAL.meta.h;
  const showCheck = e.status === 'completed' && width >= BAR_REVEAL.check.w && !flagged;
  const showDue = e.due != null && e.due <= 1 && width >= BAR_REVEAL.due.w && !flagged;
  const showSepArea = showSeps && headW >= BAR_REVEAL.seps.w && hPx >= BAR_REVEAL.seps.h;
  // Artwork PDF couldn't be found in Cloudflow (storage / file-naming issue) —
  // flag it where the ink strip would be, instead of drawing a misleading strip.
  const artworkMissing = e.artworkStatus === 'not_found' || e.artworkStatus === 'error';
  // Empty seps = artwork not read yet (still queued in the concurrency-1 fetch)
  // or nothing printable in it. Draw NOTHING rather than a placeholder strip —
  // a guessed CMYK block is indistinguishable from a real four-colour job.
  const showSepStrip = showSepArea && !artworkMissing && e.seps.length > 0;
  const showArtworkFlag = showSepArea && artworkMissing;
  const dueCol = (e.due ?? 9) <= 0 ? TOKENS.bad : TOKENS.warn;

  return (
    <Box
      role="button"
      tabIndex={0}
      data-no-pan="1"
      aria-label={`${e.job}${e.passCount > 1 ? ` pass ${e.pass} of ${e.passCount}` : ''} ${e.cust} ${e.desc}`}
      onMouseEnter={(ev) => onHover(e, ev)}
      onMouseMove={(ev) => onHover(e, ev)}
      onMouseLeave={onLeave}
      onPointerDown={onPointerDown}
      onClick={(ev) => {
        ev.stopPropagation();
        if (didDragRef.current) { didDragRef.current = false; return; }
        onSelect(e.id);
        onBarMenu(e, ev.currentTarget);
      }}
      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onSelect(e.id); onBarMenu(e, ev.currentTarget); } }}
      sx={{
        position: 'absolute', left, width, top, height: hPx,
        // Fill is drawn by the segment box(es) below; the envelope stays
        // transparent so a paused run shows the closure through the gap.
        background: 'transparent', color: st.text,
        // A split bar's envelope spans the pause too, so leaving it hit-testable
        // would swallow clicks aimed at the closure underneath. Hand hit-testing
        // to the segment fills instead (events still bubble up to these handlers).
        pointerEvents: isSplit ? 'none' : 'auto',
        // Selection / flag ring moves to the segments when split — a ring drawn
        // across the gap would read as if the run continued through the closure.
        border: isSplit
          ? '1px solid transparent'
          : selected
            ? `2px solid ${TOKENS.accent}`
            : flagged
              ? `2px solid ${TOKENS.bad}`
              : '1px solid transparent',
        borderRadius: `${TOKENS.radiusBar}px`,
        boxSizing: 'border-box', px: '11px', py: '9px',
        overflow: 'hidden', cursor: movable ? 'grab' : 'pointer',
        touchAction: 'none',
        // Ink-filter fade sits below drag feedback but above the completed
        // fade — a dimmed bar still hovers back to full for inspection.
        opacity: dragging ? 0.4 : inkDimmed ? 0.25 : e.status === 'completed' ? 0.6 : 1,
        zIndex: selected ? 9 : 2,
        transform: shiftPx ? `translateX(${shiftPx}px)` : undefined,
        // Ease-out so the bar accelerates away and settles, which reads as being
        // pushed. The stagger only applies while opening the gap; closing it
        // runs at zero delay so the lane snaps back together promptly.
        transition: `box-shadow .12s, transform ${GAP_ANIM_MS}ms cubic-bezier(.22,.9,.28,1)`,
        transitionDelay: shiftPx ? `0s, ${shiftDelayMs}ms` : undefined,
        '&:hover': { transform: `translate(${shiftPx}px, -1px)`, zIndex: 8, ...(inkDimmed ? { opacity: 1 } : {}) },
        '&:focus-visible': { outline: `2px solid ${TOKENS.accent}`, outlineOffset: 1 },
      }}
    >
      {/* run fill — one box filling the envelope, or several split around
          closed downtime bands (the gaps let the closure show through). */}
      {isSplit
        ? segRects.map((r, i) => (
            <Box key={`seg${i}`} sx={{
              position: 'absolute', top: 0, bottom: 0, left: r.x, width: r.w,
              background: st.fill,
              border: selected
                ? `2px solid ${TOKENS.accent}`
                : flagged
                  ? `2px solid ${TOKENS.bad}`
                  : st.border ? `1px solid ${st.border}` : undefined,
              borderRadius: `${TOKENS.radiusBar}px`,
              boxSizing: 'border-box',
              boxShadow: selected ? TOKENS.shadowMd : TOKENS.shadowSm,
              // The only hit-testable part of a split bar — the gap between
              // segments belongs to the closure band underneath.
              pointerEvents: 'auto',
            }} />
          ))
        : (
            <Box sx={{
              position: 'absolute', inset: 0, background: st.fill,
              border: st.border ? `1px solid ${st.border}` : undefined,
              borderRadius: `${TOKENS.radiusBar}px`,
              boxShadow: selected ? TOKENS.shadowMd : TOKENS.shadowSm,
            }} />
          )}
      {e.status === 'in_progress' && e.progress != null && (
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: e.progress * width,
          background: 'rgba(255,255,255,0.18)', borderRight: '2px solid rgba(255,255,255,0.6)',
          pointerEvents: 'none',
        }} />
      )}
      {flagged && width >= BAR_REVEAL.warn.w && (
        <Box title={flags!.join(', ')} sx={{
          position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%',
          background: TOKENS.bad, color: '#fff', display: 'grid', placeItems: 'center', zIndex: 2,
          pointerEvents: 'auto',
        }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 11 }} />
        </Box>
      )}
      {showDue && (
        <Box sx={{
          position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%',
          background: dueCol, boxShadow: `0 0 0 2px ${st.fill}`,
        }} />
      )}
      {showJob && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative', zIndex: 1 }}>
          <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{e.job}</Box>
          {/* Two-pass work puts TWO bars on the board carrying the SAME job
              number, so without this badge they're indistinguishable. Only shown
              when the job actually has a second pass — ordinary work stays clean. */}
          {e.passCount > 1 && (
            <Box
              component="span"
              title={`Pass ${e.pass} of ${e.passCount} — the two passes are scheduled independently`}
              sx={{
                fontFamily: TOKENS.monoFont, fontSize: 9.5, fontWeight: 800, lineHeight: 1,
                letterSpacing: '.3px', padding: '2px 4px', borderRadius: '3px',
                border: '1px solid currentColor', opacity: 0.85, flexShrink: 0,
              }}
            >
              P{e.pass}
            </Box>
          )}
          {showCust && <Box component="span" sx={{ fontSize: 11, fontWeight: 600, opacity: 0.92 }}>{e.cust}</Box>}
          {showCheck && <CheckIcon sx={{ fontSize: 13, ml: 'auto' }} />}
        </Box>
      )}
      {showDesc && (
        <Box sx={{ fontSize: 11, fontWeight: 500, opacity: 0.9, lineHeight: 1.2, mt: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', position: 'relative', zIndex: 1 }}>{e.desc}</Box>
      )}
      {showDesc && e.ref && (
        <Box sx={{ fontFamily: TOKENS.monoFont, fontSize: 9.5, fontWeight: 600, opacity: 0.7, letterSpacing: '.2px', lineHeight: 1.2, mt: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', position: 'relative', zIndex: 1 }}>{e.ref}</Box>
      )}
      {showMeta && (
        <Box sx={{ fontFamily: TOKENS.monoFont, fontSize: 9.5, opacity: 0.8, mt: '2px', fontVariantNumeric: 'tabular-nums', position: 'relative', zIndex: 1 }}>
          {e.sheets.toLocaleString()} sheets · {fmtClock(e.start)}–{fmtClock(e.start + e.planned_total)}
          {/* Coating rides the meta line rather than earning its own row: it only
              matters when there IS one, and 'NONE' would be pure noise. */}
          {hasCoating(e.coating) && ` · ${e.coating}`}
        </Box>
      )}
      {showSepStrip && (
        <Box sx={{ display: 'flex', gap: '2px', position: 'absolute', left: 11, bottom: 6 }}>
          {e.seps.map((s, i) => (
            <Box key={i} sx={{
              width: 10, height: 11, borderRadius: '2px', background: s.hex,
              border: s.role === 'coating' ? '1px dashed rgba(0,0,0,.45)' : '0.5px solid rgba(0,0,0,.2)',
            }} />
          ))}
        </Box>
      )}
      {showArtworkFlag && (
        <Box
          title={`Artwork not found in Cloudflow${e.artworkPath ? `\n${e.artworkPath}` : ''}`}
          sx={{
            position: 'absolute', left: 11, bottom: 5, display: 'flex', alignItems: 'center', gap: '3px',
            px: '4px', height: 13, borderRadius: '3px', pointerEvents: 'auto',
            background: 'rgba(214,120,20,0.18)', border: `1px solid ${TOKENS.warn}`,
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 10, color: TOKENS.warn }} />
          <Box sx={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '.3px', color: TOKENS.warn }}>NO ARTWORK</Box>
        </Box>
      )}
    </Box>
  );
}
